import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, MapPin, Calendar, FolderOpen } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function PortfolioPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');


  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/public/projects?limit=50`);
        if (response.data.items?.length > 0) {
          setProjects(response.data.items);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const categories = ['all', 'Festival', 'Event Series', 'Brand Activation', 'VIP Event', 'Corporate', 'Cultural Event'];

  const filteredProjects = projects.filter(project => {
    return filter === 'all' || project.category === filter;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="portfolio-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              {t('portfolio.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('portfolio.title')}
            </motion.h1>

            {/* Filters */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                    filter === cat
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat === 'all' ? t('portfolio.filter_all') : cat}
                </button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-dark py-16" data-testid="projects-grid">
        <div className="container-premium">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-video loading-skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredProjects.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {filteredProjects.map((project, index) => (
                <motion.div 
                  key={project.project_id || project.slug} 
                  variants={fadeInUp}
                  className={index === 0 ? 'md:col-span-2' : ''}
                >
                  <Link to={`/portfolio/${project.slug}`} className="block group" data-testid={`project-${project.slug}`}>
                    <div className={`relative overflow-hidden ${index === 0 ? 'aspect-[21/9]' : 'aspect-video'}`}>
                      <img
                        src={project.cover_image || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80'}
                        alt={project.title?.[lang] || project.title?.es}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
                      
                      {project.featured && (
                        <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-black text-xs font-medium rounded-full">
                          Featured
                        </div>
                      )}

                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full border-2 border-white flex items-center justify-center">
                          <Play size={24} className="text-white ml-1" fill="white" />
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-4 mb-3">
                          <span className="text-[#D4AF37] text-xs tracking-wider uppercase">
                            {project.category}
                          </span>
                          <span className="flex items-center gap-1 text-white/60 text-xs">
                            <MapPin size={12} />
                            {project.city}
                          </span>
                          {project.date && (
                            <span className="flex items-center gap-1 text-white/60 text-xs">
                              <Calendar size={12} />
                              {new Date(project.date).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'short' })}
                            </span>
                          )}
                        </div>
                        <h3 className={`font-display text-white ${index === 0 ? 'text-3xl md:text-4xl' : 'text-2xl'}`}>
                          {project.title?.[lang] || project.title?.es}
                        </h3>
                        {project.summary && (
                          <p className="text-white/60 text-sm mt-2 line-clamp-2">
                            {project.summary?.[lang] || project.summary?.es}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <FolderOpen size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50">No se encontraron proyectos</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-subtle" data-testid="portfolio-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl text-white mb-4">
              ¿Quieres que tu proyecto sea el próximo?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 mb-8">
              Trabajemos juntos para crear algo extraordinario
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-gold">
                Iniciar Proyecto
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
